import type { AnalyticsReport, FunnelRow, MentorStat, Period } from "@/app/analyticsQueries";
import type { Cohort } from "@/app/peopleQueries";
import { count, plural } from "@/lib/plural";

/** A3 «Аналитика»: правила строк воронки, темп, KPI — из макета (§3 п.6–8 спеки).
 *  Единица прогресса — сданный гейт; процентов просмотра здесь нет и не будет. */

export const SCOPE_ALL = "all";
export const SCOPE_NONE = "nocohort";

export const PERIODS: readonly { id: Period; label: string; weeks: number }[] = [
  { id: "4w", label: "4 недели", weeks: 4 },
  { id: "12w", label: "12 недель", weeks: 12 },
  { id: "all", label: "всё время", weeks: 0 },
];

export type Tab = { id: string; label: string };

/** Вкладки когорт A3: все / когорты (включая завершённые, без набора без людей) / без когорты. */
export function cohortTabs(cohorts: Cohort[] | undefined): Tab[] {
  const cs = (cohorts ?? []).filter((c) => c.students > 0 || c.status !== "recruiting");
  return [{ id: SCOPE_ALL, label: "все" }, ...cs.map((c) => ({ id: c.id, label: c.name.replace(/^ML-/, "") })), { id: SCOPE_NONE, label: "без когорты" }];
}

export function scopeLabel(scope: string, cohorts?: Cohort[]): string {
  if (scope === SCOPE_ALL) return "Все студенты";
  if (scope === SCOPE_NONE) return "Без когорты";
  return cohorts?.find((c) => c.id === scope)?.name ?? "Когорта";
}

export type Tone = "ok" | "warn" | "err" | "mut" | "ink" | "ink2" | "acc";

/** Полоса «сдало гейт»: ≥80 ok / ≥50 warn / иначе err; 0 — пустая. */
export type BarTone = "ok" | "warn" | "err" | "none";
export const barTone = (pct: number): BarTone => (pct <= 0 ? "none" : pct >= 80 ? "ok" : pct >= 50 ? "warn" : "err");
/** Дни на шаге: ≥12 err / ≥9 warn. */
export const daysTone = (d: number): Tone => (d >= 12 ? "err" : d >= 9 ? "warn" : "mut");
/** Итерации: ≥2.5 err / ≥2 warn. */
export const iterTone = (it: number): Tone => (it >= 2.5 ? "err" : it >= 2 ? "warn" : "mut");
export const fmtIter = (it: number) => (it > 0 ? it.toFixed(1) : "—");
export const fmtDays = (d: number) => (d > 0 ? `${Math.round(d)} дн.` : "—");
export const TONE_TEXT: Record<Tone, string> = { ok: "text-ok", warn: "text-warn", err: "text-err", mut: "text-mut", ink: "text-ink", ink2: "text-ink2", acc: "text-acc" };
export const BAR_BG: Record<BarTone, string> = { ok: "bg-ok", warn: "bg-warn", err: "bg-err", none: "bg-card2" };

/** «дошло» — N / размер старта. */
export const reachedLabel = (row: FunnelRow, top: number) => `${row.reached} / ${top}`;

/** Медиана ответа по менторам с вердиктами (нижняя медиана медиан), часы. 0 — вердиктов нет. */
export function mentorsMedianHours(mentors: MentorStat[]): number {
  const xs = mentors.filter((m) => m.verdicts > 0).map((m) => m.median_hours).sort((a, b) => a - b);
  return xs.length ? xs[Math.floor((xs.length - 1) / 2)]! : 0;
}

export const fmtHours = (h: number) => (h <= 0 ? "—" : h < 1 ? "< 1 ч" : `${Math.round(h)} ч`);
/** Ответ ментора: ≤ 4 ч — хорошо, дольше суток (SLA) — плохо. */
export const hoursTone = (h: number): Tone => (h <= 0 ? "mut" : h <= 4 ? "ok" : h > 24 ? "err" : "ink");
export const fmtLate = (share: number) => (share > 0 ? `${Math.round(share * 100)}%` : "нет");

export type Kpi = { v: string; label: string; note: string; tone: Tone; testId: string };

/** Четыре карточки: студентов, гейтов (всего / за период), медиана ответа, итераций. */
export function kpis(r: AnalyticsReport, scope: string, period: Period): Kpi[] {
  const shownWeeks = r.pace.length;
  const periodWeeks = PERIODS.find((p) => p.id === period)?.weeks ?? 0;
  const n = period === "all" ? r.weeks : Math.min(periodWeeks, shownWeeks || periodWeeks);
  const gates: Kpi =
    period === "all"
      ? { v: String(r.gates_total), label: plural(r.gates_total, "гейт сдан", "гейта сдано", "гейтов сдано"), note: r.weeks > 0 ? `за ${count(r.weeks, "неделю", "недели", "недель")} от старта` : "за всё время", tone: "acc", testId: "kpi-gates" }
      : { v: `+${r.gates_in_period}`, label: "гейтов за период", note: `последние ${count(n, "неделя", "недели", "недель")} · всего ${r.gates_total}`, tone: "acc", testId: "kpi-gates" };
  const med = mentorsMedianHours(r.mentors);
  const withVerdicts = r.mentors.filter((m) => m.verdicts > 0).length;
  return [
    {
      v: String(r.students),
      label: plural(r.students, "студент", "студента", "студентов"),
      note: scope === SCOPE_ALL ? "в когортах и вне их" : scope === SCOPE_NONE ? "ведутся индивидуально" : r.weeks > 0 ? `${r.weeks}-я неделя` : "ещё не началась",
      tone: "ink",
      testId: "kpi-students",
    },
    gates,
    {
      v: fmtHours(med),
      label: "медиана ответа менторов",
      note: withVerdicts ? `${count(withVerdicts, "ментор", "ментора", "менторов")} с вердиктами · SLA 24 ч` : "вердиктов за период нет",
      tone: hoursTone(med),
      testId: "kpi-response",
    },
    { v: fmtIter(r.iterations), label: "итераций на сдачу", note: r.iterations > 0 ? "в среднем до «принято»" : "принятых сдач пока нет", tone: r.iterations > 0 ? iterTone(r.iterations) : "mut", testId: "kpi-iterations" },
  ];
}

/** Подпись к воронке: «ML-осень-2026 · 14 студентов · 6-я неделя». */
export function funnelNote(r: AnalyticsReport, scope: string, cohorts?: Cohort[]): string {
  const tail = scope === SCOPE_ALL ? "в когортах и вне их" : scope === SCOPE_NONE ? "без общего старта" : r.weeks > 0 ? `${r.weeks}-я неделя` : "ещё не началась";
  return `${scopeLabel(scope, cohorts)} · ${count(r.students, "студент", "студента", "студентов")} · ${tail}`;
}

/** Темп: подпись под графиком. При периоде короче истории — текст макета «Показаны последние N недель из M…». */
export function paceNote(r: AnalyticsReport, period: Period): string {
  if (r.pace.length === 0) return "Нет общего старта — темп по неделям считается от старта когорты.";
  if (period !== "all" && r.pace.length < r.weeks) {
    const n = r.pace.length;
    return `Показаны последние ${count(n, "неделя", "недели", "недель")} из ${r.weeks}. Переключи на «всё время», чтобы увидеть темп с первой недели.`;
  }
  const max = r.pace.reduce((a, p) => (p.gates > a.gates ? p : a), r.pace[0]!);
  const min = r.pace.reduce((a, p) => (p.gates < a.gates ? p : a), r.pace[0]!);
  if (max.gates === min.gates) return `Ровный темп: ${count(max.gates, "гейт", "гейта", "гейтов")} в неделю.`;
  return `Пик — ${max.week}-я неделя (${count(max.gates, "гейт", "гейта", "гейтов")}), минимум — ${min.week}-я (${min.gates}).`;
}

export function paceTotal(r: AnalyticsReport): string {
  const sum = r.pace.reduce((a, p) => a + p.gates, 0);
  return `${sum} за ${count(r.pace.length, "неделю", "недели", "недель")}`;
}

/** Высота столбика (px) и тон: ≤ 40 % от максимума — предупреждение. */
export function paceBar(gates: number, max: number, h = 96): { h: number; warn: boolean } {
  if (max <= 0) return { h: 6, warn: false };
  return { h: Math.max(6, Math.round((gates / max) * h)), warn: gates <= max * 0.4 };
}

export type FeedbackRow = { step: FunnelRow["step"]; avg: number; n: number };

/** Отзывы по шагам: соединяем с воронкой по step_id, порядок маршрута, только где есть отзывы. */
export function joinFeedback(r: AnalyticsReport): FeedbackRow[] {
  const by = new Map(r.feedback.map((f) => [f.step_id, f]));
  return r.funnel.flatMap((row) => {
    const f = by.get(row.step.id);
    return f && f.n > 0 ? [{ step: row.step, avg: f.avg, n: f.n }] : [];
  });
}

export const fmtAvg = (avg: number) => avg.toFixed(1);

/** Имя выгрузки: funnel-<когорта>-<дата>.csv (как у сервера). */
export function csvName(scope: string, cohorts: Cohort[] | undefined, now = new Date()): string {
  const name = scope === SCOPE_ALL || scope === SCOPE_NONE ? scope : (cohorts?.find((c) => c.id === scope)?.name ?? scope);
  const d = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `funnel-${name}-${d}.csv`;
}

export const fmtComputed = (iso: string) => new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Выводы макета — дословно (§3 п.7). Статичны: ревью курса, не расчёт. */
export const FINDINGS = [
  { title: "Шаг «NumPy и Pandas» держит когорту вдвое дольше нормы", body: "14 дней против цели в 7 и 2.6 итерации на сдачу. Похоже, домашка на 14 задач слишком большая для одного захода.", where: "Python · шаг 2 · 5 из 10 сдали гейт" },
  { title: "Ресурсы без размеченного гайда почти не отмечают", body: "Лекция 2 и конспект менторов — 21% и 32% против 86% у лекции с гайдом по юнитам. Без разбивки студент не понимает, что считать пройденным.", where: "ML · шаг 1 · 2 ресурса без гайда" },
  { title: "Возвраты повторяются по одной теме", body: "Работа с пропусками — причина 4 из 9 возвратов в когорте. Стоит вынести в отдельный блок «типичные ошибки» шага.", where: "Python · шаг 2 · 9 возвратов" },
] as const;

/** Дословный блок — запасной текст, если API вернул пустую строку. */
export const NOT_MEASURED = "Процент просмотра видео и время в интерфейсе. Единица прогресса — сданный гейт: человек умеет делать то, что заявлено в шаге.";
